import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Chip, Typography, Tabs, Tab, Box, Tooltip, Link } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppDispatch, useAppSelector } from '../reduxStore/Store';
import {
  fetchStockData,
  fetchStockMouvements,
  setFilters,
  clearStockData,
  clearMouvementData,
} from '../reduxStore/StockSlice';
import { StockArticle, StockMouvement } from '../models/models';
import { TableHeader } from '../constants/interfaces';
import { FormatRapport, PERMISSION_PRIX_REVIENT, TypeRapport } from '../constants/enums';
import { useGenerateReport } from '../hooks/useGenerateReport';
import { usePermission } from '../hooks/usePermission';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import PrintMenuButton from '../components/PrintMenuButton';
import MyTable from '../components/MyTable';
import StockFilters from '../components/Filters/StockFilters';
import { formatQuantite } from '../utils/quantiteUtils';
import { formatNumberWithSpaces } from '../utils/utils';
import { getLabels } from '../constants/fieldLabels';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import CompteResultatPanel from '../components/CompteResultatPanel';
import SituationPatrimonialePanel from '../components/SituationPatrimonialePanel';
import StatVentesPanel from '../components/StatVentesPanel';

type TabKey = 'etat' | 'mouvements' | 'resultat' | 'patrimoine' | 'ventes';

// Onglets « rapport » — pas de tableau de stock ni d'impression, panneau autonome.
const RAPPORT_TABS: TabKey[] = ['resultat', 'patrimoine', 'ventes'];

const formatNum = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString('fr-FR', { maximumFractionDigits: 4 }) : '0';

/** Colonnes au coût — masquées sans le droit articles.prixRevient (0156). */
const COLONNES_COUT = ['prix_r', 'valorisation_achat', 'valeur_mouvement'];

const StockAndStat: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    stockData, total,
    totalValoAchat, totalValoVente, totalValoVenteTtc, totalQuantite,
    mouvementData, totalMouvement,
    totalEntreesQte, totalSortiesQte, totalEntreesValeur, totalSortiesValeur,
    loading, error, filters,
  } = useAppSelector((state) => state.StockSlice);

  const [activeTab, setActiveTab] = useState<TabKey>('etat');
  const selectedDepartement = useSelector((state: any) => state.AppSlice.selectedDepartement);
  // Sans le droit de voir le coût (0156) : l'API renvoie null → colonnes et totaux au coût retirés
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);
  const sansCout = <T,>(headers: TableHeader<T>[]): TableHeader<T>[] =>
    voitCout ? headers : headers.filter((h) => !COLONNES_COUT.includes(String(h.key)));
  const { generateReport, generating } = useGenerateReport();
  const navigate = useNavigate();

  // Au mount (et quand le contexte global change), aligne `filter_dept_id` sur
  // le dept courant du contexte. Permet en mode dept de continuer à voir le
  // dept courant par défaut, et en mode ALL d'avoir "Tous" (null) par défaut.
  // L'utilisateur peut ensuite changer librement via le filtre.
  // Dispatch uniquement si la valeur change : un setFilters inutile recrée l'objet
  // `filters` et relance un fetch identique (sp_select_stock = coûteux en CPU).
  // Quand la valeur change, les filtres courants deviennent périmés : on les mémorise
  // pour que l'effet de fetch ne parte pas avec (ex. après F5 : filtre vide → appel
  // « Tous » inutile, refusé en 403 pour un user restreint). Le fetch suivant, avec
  // le bon département, part normalement après le debounce.
  const staleFilters = useRef<typeof filters | null>(null);
  useEffect(() => {
    const deptId = selectedDepartement?.id ?? null;
    if (filters.filter_dept_id !== deptId) {
      staleFilters.current = filters;
      dispatch(setFilters({ filter_dept_id: deptId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartement?.id]);

  const handleTabChange = (_: React.SyntheticEvent, v: TabKey) => {
    setActiveTab(v);
    dispatch(setFilters({ offset: 0 }));   // repartir de la page 1 sur l'autre onglet
  };

  const handleGenerateReport = (format: FormatRapport) => {
    // pagination retirée — le worker traite avec limit 999999
    // Le rapport reflète le filtre page (filter_dept_id), pas le contexte global.
    const { limit, offset, filter_dept_id, ...filterParams } = filters;
    const reportDeptId = filter_dept_id ?? selectedDepartement?.id ?? null;
    const isEtat = activeTab === 'etat';
    generateReport({
      type_rapport:   isEtat ? TypeRapport.STOCK : TypeRapport.MOUVEMENT_STOCK,
      format,
      parametres:     { ...filterParams, departement_id: filter_dept_id },
      departement_id: reportDeptId,
      fileName:       `${isEtat ? 'etat_stock' : 'mouvements_stock'}_${format.toLowerCase()}`,
    });
  };

  // Debounce : évite un fetch à chaque keystroke (date input notamment) et à chaque
  // clic de pagination rapproché — seul le dernier état part après 400ms de calme.
  // Le state Redux est mis à jour immédiatement (UI reste réactive), seul le fetch
  // est retardé.
  const debouncedFilters = useDebouncedValue(filters, 400);

  useEffect(() => {
    // Filtres périmés par la synchro du département → pas de fetch (cf. staleFilters)
    if (debouncedFilters === staleFilters.current) {
      staleFilters.current = null;
      return;
    }
    // L'onglet « Compte de résultat » gère son propre chargement (CompteResultatPanel).
    const request =
      activeTab === 'etat'       ? dispatch(fetchStockData(debouncedFilters)) :
      activeTab === 'mouvements' ? dispatch(fetchStockMouvements(debouncedFilters)) :
      null;
    // Nouvelle page/filtre ou démontage : la requête en cours est obsolète → annulée,
    // sa réponse ne peut plus écraser celle de la dernière page demandée.
    // Pas de clear ici : vider les données à chaque page ferait basculer MyTable sur
    // « Aucune donnée » (hauteur différente) → la page sauterait. Le squelette couvre l'attente.
    return () => { request?.abort(); };
  }, [dispatch, debouncedFilters, activeTab]);

  // Nettoyage uniquement en quittant la page.
  useEffect(() => {
    return () => {
      dispatch(clearStockData());
      dispatch(clearMouvementData());
    };
  }, [dispatch]);

  // Squelette dès le clic (pendant le debounce) et jusqu'à la réponse serveur.
  const tableLoading = loading || filters !== debouncedFilters;

  const handlePageChange = (page: number, pageSize: number) => {
    dispatch(setFilters({ offset: page * pageSize, limit: pageSize }));
  };

  // ── Headers : État du stock ────────────────────────────────────────────────
  // noTruncate : codes/quantités/montants jamais coupés. Le texte (désignation,
  // famille) absorbe la compression. defaultVisible:false → masqué par défaut
  // (révélable via le bouton Colonnes) pour ne jamais déborder.
  const headersEtat: TableHeader<StockArticle>[] = [
    { key: 'code',           label: 'Code', noTruncate: true, width: 90 },
    { key: 'designation',    label: 'Désignation' },
    { key: 'famille_nom',    label: 'Famille' },
    { key: 'unite',          label: 'Unité', noTruncate: true, width: 70, defaultVisible: false },
    { key: 'stock_securite', label: 'Seuil sécu.', align: 'right', noTruncate: true, width: 95, defaultVisible: false, render: (row) => formatQuantite(row.stock_securite, row) },
    {
      key: 'stock_reel',
      label: 'Stock réel',
      align: 'right',
      noTruncate: true,
      width: 110,
      render: (row) => (
        <Chip
          label={formatQuantite(row.stock_reel, row)}
          color={row.stock_reel < row.stock_securite ? 'error' : 'success'}
          size="small"
          variant="outlined"
        />
      ),
    },
    { key: 'total_entrees', label: 'Entrées', align: 'right', noTruncate: true, width: 90, defaultVisible: false, render: (row) => formatQuantite(row.total_entrees, row) },
    { key: 'total_sorties', label: 'Sorties', align: 'right', noTruncate: true, width: 90, defaultVisible: false, render: (row) => formatQuantite(row.total_sorties, row) },
    { key: 'prix_r',        label: 'Prix achat', align: 'right', noTruncate: true, width: 95, defaultVisible: false },
    { key: 'prix_v',        label: 'Prix vente HT', align: 'right', noTruncate: true, width: 95 },
    { key: 'valorisation_achat', label: 'Valo. achat', align: 'right', noTruncate: true, width: 110, defaultVisible: false, render: (row) => formatNumberWithSpaces(row.valorisation_achat) },
    { key: 'valorisation_vente', label: 'Valo. vente HT', align: 'right', noTruncate: true, width: 110, render: (row) => formatNumberWithSpaces(row.valorisation_vente) },
    { key: 'valorisation_vente_ttc', label: 'Valo. vente TTC', align: 'right', noTruncate: true, width: 115, defaultVisible: false, render: (row) => formatNumberWithSpaces(row.valorisation_vente_ttc) },
  ];

  // ── Headers : Mouvements de stock ──────────────────────────────────────────
  // Quand le filtre dept est "Tous" (filter_dept_id === null), on ajoute une
  // colonne "Département" car les mouvements proviennent de plusieurs depts.
  const showDeptColumn = filters.filter_dept_id === null;
  const headersMouvement: TableHeader<StockMouvement>[] = [
    { key: 'date_doc',       label: 'Date', noTruncate: true, width: 95, render: (r) => r.date_doc?.split('T')[0] ?? '' },
    {
      key: 'numero_doc',
      label: 'N° doc',
      noTruncate: true,
      width: 120,
      // Cliquable → accès direct au document (route par id, jamais par numero_doc
      // seul — cf. la règle établie sur la non-unicité de numero_doc par owner).
      render: (r) => {
        if (!r.numero_doc) return '';
        return (
          <Tooltip title="Ouvrir le document">
            <Link
              component="button"
              type="button"
              underline="hover"
              sx={{ fontWeight: 600, textAlign: 'left' }}
              onClick={() => navigate(`/facturation/${r.document_vente_id}`)}
            >
              {r.numero_doc}
            </Link>
          </Tooltip>
        );
      },
    },
    { key: 'type_doc_code',  label: 'Type', width: 100, render: (r) => r.type_doc_libelle || r.type_doc_code },
    { key: 'code',           label: 'Article', render: (r) => `${r.code} — ${r.designation}` },
    ...(showDeptColumn
      ? [{ key: 'departement_nom' as const, label: 'Département', width: 120 }]
      : []),
    { key: 'depot_nom',      label: 'Dépôt', width: 110, defaultVisible: false },
    {
      key: 'quantite_signee',
      label: 'Qté',
      align: 'right',
      noTruncate: true,
      width: 115,
      render: (r) => (
        <Chip
          label={`${r.signe > 0 ? '+' : ''}${formatNum(Number(r.quantite_signee))}${r.unite ? ' ' + r.unite : ''}`}
          color={r.signe > 0 ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      key: 'valeur_mouvement',
      label: 'Valeur',
      align: 'right',
      noTruncate: true,
      width: 120,
      render: (r) => (
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 600, color: r.signe > 0 ? 'success.main' : 'error.main' }}
        >
          {r.signe > 0 ? '+' : ''}{formatNumberWithSpaces(Number(r.valeur_mouvement))}
        </Typography>
      ),
    },
    { key: 'stock_cumule',   label: 'Stock cumulé', align: 'right', noTruncate: true, width: 105, render: (r) => formatNum(Number(r.stock_cumule)) },
    { key: 'tiers_nom',      label: 'Tiers', defaultVisible: false },
    { key: 'user_login',     label: 'Utilisateur', width: 110, defaultVisible: false },
  ];

  const headerTitle =
    activeTab === 'etat' ? 'État du stock'
    : activeTab === 'mouvements' ? 'Mouvements de stock'
    : activeTab === 'resultat' ? 'Compte de résultat'
    : activeTab === 'patrimoine' ? 'Situation patrimoniale'
    : 'Stats de vente';
  const headerIcon =
    activeTab === 'etat' ? <WarehouseIcon />
    : activeTab === 'mouvements' ? <SwapVertIcon />
    : activeTab === 'resultat' ? <BarChartIcon />
    : activeTab === 'patrimoine' ? <AccountBalanceWalletIcon />
    : <TrendingUpIcon />;
  const headerSubtitle =
    activeTab === 'etat' ? `${total} article${total > 1 ? 's' : ''}`
    : activeTab === 'mouvements' ? `${totalMouvement} mouvement${totalMouvement > 1 ? 's' : ''}`
    : undefined;

  const isRapportTab = RAPPORT_TABS.includes(activeTab);

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        icon={headerIcon}
        actions={
          !isRapportTab ? (
            <PrintMenuButton
              onPdf={()   => handleGenerateReport(FormatRapport.PDF)}
              onExcel={() => handleGenerateReport(FormatRapport.EXCEL)}
              loading={generating}
            />
          ) : undefined
        }
      />

      {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      <AppSurface sx={{ mb: 2 }} padding={0}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="etat"        label="État du stock"        icon={<WarehouseIcon />}            iconPosition="start" />
          <Tab value="mouvements"  label="Mouvements de stock"  icon={<SwapVertIcon />}             iconPosition="start" />
          <Tab value="ventes"      label="Stats de vente"       icon={<TrendingUpIcon />}           iconPosition="start" />
          <Tab value="resultat"    label="Compte de résultat"   icon={<BarChartIcon />}             iconPosition="start" />
          <Tab value="patrimoine"  label="Situation patrimoniale" icon={<AccountBalanceWalletIcon />} iconPosition="start" />
        </Tabs>
        {!isRapportTab && (
          <Box sx={{ p: 2 }}>
            <StockFilters
              showSousSecurite={activeTab === 'etat'}
              showDateRange={activeTab === 'mouvements'}
            />
          </Box>
        )}
      </AppSurface>

      {activeTab === 'resultat' ? (
        <CompteResultatPanel />
      ) : activeTab === 'patrimoine' ? (
        <SituationPatrimonialePanel />
      ) : activeTab === 'ventes' ? (
        <StatVentesPanel />
      ) : (
        <AppSurface>
          {activeTab === 'etat' ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  px: 2,
                  py: 1.5,
                  mb: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Inventory2Icon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Quantité totale :
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatNum(totalQuantite)}
                  </Typography>
                  <Tooltip title="Quantité, toutes unités confondues">
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                  </Tooltip>
                </Box>
                {voitCout && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceWalletIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Valorisation totale (achat) :
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {formatNumberWithSpaces(totalValoAchat)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Valorisation totale (vente HT) :
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatNumberWithSpaces(totalValoVente)}
                  </Typography>
                </Box>
                {/* Affichée seulement si une TVA existe dans le stock filtré (sinon = vente HT) */}
                {totalValoVenteTtc !== 0 && totalValoVenteTtc !== totalValoVente && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Valorisation totale (vente TTC) :
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {formatNumberWithSpaces(totalValoVenteTtc)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <MyTable
                key="etat"
                tableKey="stock_etat"
                headers={sansCout(headersEtat)}
                data={stockData}
                totalRows={total}
                loading={tableLoading}
                onPageChange={handlePageChange}
                defaultPageSize={filters.limit}
                size="small"
                showDetails
                fieldLabels={getLabels('StockArticle')}
                totals={{
                  stock_reel:         formatNum(totalQuantite),
                  valorisation_achat: formatNumberWithSpaces(totalValoAchat),
                  valorisation_vente: formatNumberWithSpaces(totalValoVente),
                  valorisation_vente_ttc: formatNumberWithSpaces(totalValoVenteTtc),
                }}
              />
            </>
          ) : (
            <>
              {/* Bandeau récap : entrées / sorties / net (qté + valeur), hors pagination */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  px: 2,
                  py: 1.5,
                  mb: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {([
                  ['Entrées', totalEntreesQte,                      totalEntreesValeur,                      'success.main'],
                  ['Sorties', totalSortiesQte,                      totalSortiesValeur,                      'error.main'],
                  ['Net',     totalEntreesQte - totalSortiesQte,    totalEntreesValeur - totalSortiesValeur, 'text.primary'],
                ] as const).map(([label, qte, valeur, color]) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">{label} :</Typography>
                    {/* Sans le droit de voir le coût : quantité seule (valeur au coût = null) */}
                    {voitCout && (
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
                        {formatNumberWithSpaces(valeur)}
                      </Typography>
                    )}
                    <Tooltip title="Quantité, toutes unités confondues">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: 'text.disabled' }}>
                        <Typography variant="caption">({formatNum(qte)})</Typography>
                        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                      </Box>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
              <MyTable
                key="mouvements"
                tableKey="stock_mouvements"
                headers={sansCout(headersMouvement)}
                data={mouvementData}
                totalRows={totalMouvement}
                loading={tableLoading}
                onPageChange={handlePageChange}
                defaultPageSize={filters.limit}
                size="small"
                showDetails
                fieldLabels={getLabels('StockMouvement')}
                totals={{
                  quantite_signee:  formatNum(totalEntreesQte - totalSortiesQte),
                  valeur_mouvement: formatNumberWithSpaces(totalEntreesValeur - totalSortiesValeur),
                }}
              />
            </>
          )}
        </AppSurface>
      )}
    </AppPage>
  );
};

export default StockAndStat;
